import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { usePublicChangelog } from "@/api/queries/publicChangelog";

/** How many releases the landing shows. More would be a second index page. */
const SHOWN = 3;

/**
 * The last three releases, as evidence the product is alive.
 *
 * For a small shop deciding whether to depend on a platform, "somebody
 * shipped something last week" is a real signal, and it costs nothing —
 * the articles are already written and already public at ``/changelog``.
 *
 * Two deliberate properties:
 *
 * - **One source.** Same hook as the changelog index, so a published
 *   article cannot appear in one place and not the other.
 * - **Silence over an empty shelf.** Under three published items in the
 *   visitor's language the section does not render at all. A marketing
 *   block reading "1 release" says the opposite of what it is for, and
 *   falling back to another language would put English on the Czech
 *   page — which is the defect this page has already been through
 *   (KAN-704, and the pricing rows in KAN-46).
 */
export function PlatformChangelog({ lang }: { lang: string }): JSX.Element | null {
  const { t } = useTranslation("common");
  const { data } = usePublicChangelog(lang, null);

  const items = (data?.items ?? []).slice(0, SHOWN);
  if (items.length < SHOWN) return null;

  return (
    <section className="section" data-testid="platform-changelog">
      <div className="inner">
        <div className="head-row" style={{ marginBottom: 0 }}>
          <div>
            <p className="kicker">{t("platformLanding.changelog.kicker")}</p>
            <h2 className="h2" style={{ maxWidth: 560 }}>
              {t("platformLanding.changelog.heading")}
            </h2>
          </div>
          <Link
            to={`/${lang}/changelog`}
            data-testid="platform-changelog-all"
            className="news-all"
            style={{ justifySelf: "end" }}
          >
            {t("platformLanding.changelog.all")}
          </Link>
        </div>

        <ul className="news">
          {items.map((item) => (
            <li key={item.slug}>
              <Link to={`/${lang}/changelog/${item.slug}`}>
                {item.released_at && (
                  <time dateTime={item.released_at}>
                    {new Date(item.released_at).toLocaleDateString(lang)}
                  </time>
                )}
                <b>{item.title}</b>
                {item.excerpt && <span>{item.excerpt}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
